import React, { useEffect, useRef, useState } from "react";
import { Zap, Waves } from "lucide-react";
import { useTranslation } from "react-i18next";
import { buildSessionTimeline } from "utils/sessionTimeline";
import { timeTooltip } from "utils/timeDisplay";
import { severityForG } from "./impactVisuals";

const HALF = 17; // px above (impacts) and below (events) the axis
const TICK_W = 3;

/**
 * One session as a compact strip: a single time axis with impacts rising
 * above it and low-g events falling below, each tick's height and colour
 * carrying severity. Clicking a tick opens that record.
 *
 * Mirrored around one axis rather than stacked in two lanes — two baselines
 * and two rules of chrome cost twice the height to say the same thing, and
 * direction distinguishes the bands as clearly as separation did.
 *
 * There is deliberately no coverage bar. A record's stored window sits at the
 * same instant as its tick, so drawing coverage separately reproduced the
 * tick positions in a third row that read as more data. The one fact worth
 * stating — how little of the session holds samples — is text, and lives in
 * the session header above.
 *
 * Ticks within a few pixels are clustered (utils/sessionTimeline), so an
 * end-of-race burst stays one clickable marker rather than six overlapping
 * slivers at exactly the moment that matters.
 */
const SessionTimelineStrip = ({ session, onSelect, clusterPx = 7 }) => {
  const { t } = useTranslation();
  const wrapRef = useRef(null);
  const [width, setWidth] = useState(800);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(() => setWidth(el.clientWidth || 800));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const tl = buildSessionTimeline(session, { widthPx: width, clusterPx });
  if (!tl) return null;

  const { lanes, intervals } = tl;
  const startMs = session.recordedStartMs ?? tl.window.t0;
  const pct = (v) => `${Math.min(100, Math.max(0, v * 100))}%`;

  // The interval band only earns its pixels when it says something the strip's
  // own extent doesn't: several runs under one session, or a session that
  // never closed. A single complete interval spans the whole width by
  // definition, so drawing it is pure decoration.
  const showIntervals =
    intervals.length > 1 || intervals.some((iv) => iv.openEnded);

  const tick = (m, kind) => {
    const sev = severityForG(m.peakG, false);
    const up = kind === "impact";
    const h = Math.round(5 + Math.min(1, m.peakG / 50) * (HALF - 6));
    const label = [
      m.count > 1
        ? t("analysis.timelineStrip.clusterPeak", {
            count: m.count,
            peak: m.peakG.toFixed(1),
          })
        : t("analysis.timelineStrip.singlePeak", { peak: m.peakG.toFixed(1) }),
      m.peak?.timestampIsEpoch
        ? timeTooltip(m.peak.timestampMs, true, startMs)
        : "",
      m.count > 1
        ? t("analysis.timelineStrip.clickOpenHardest")
        : t("analysis.timelineStrip.clickOpen"),
    ]
      .filter(Boolean)
      .join("\n");
    return (
      <button
        key={`${kind}-${m.x}-${m.peak?.frameIndex ?? m.peakG}`}
        onClick={() => m.peak && onSelect?.(m.peak)}
        title={label}
        className="absolute -translate-x-1/2 rounded-sm transition-opacity duration-150 hover:opacity-70"
        style={{
          left: pct(m.x),
          [up ? "bottom" : "top"]: HALF + 1,
          height: h,
          width: m.count > 1 ? TICK_W + 1 : TICK_W,
          backgroundColor: sev.color,
        }}
      />
    );
  };

  return (
    <div className="flex items-stretch gap-2 pb-1 pt-2">
      {/* Band key: position is the distinction, so the icons only need to
          say which way is which — once, not per lane. */}
      <div
        className="flex w-3 flex-shrink-0 flex-col justify-between py-px"
        aria-hidden="true"
      >
        <Zap size={9} className="text-[#DC2626]" />
        <Waves size={9} className="text-[#0EA5E9]" />
      </div>

      <div
        ref={wrapRef}
        className="relative min-w-0 flex-1"
        style={{ height: HALF * 2 }}
      >
        {showIntervals &&
          intervals.map((iv, i) => (
            <div
              key={i}
              className="absolute inset-y-0 rounded-sm bg-[#F5F3F0]"
              style={{ left: pct(iv.x0), width: pct(iv.x1 - iv.x0) }}
              title={
                iv.openEnded
                  ? t("analysis.timelineStrip.recordingCutShort")
                  : t("analysis.timelineStrip.recordingInterval")
              }
            />
          ))}
        <div
          className="absolute inset-x-0 h-px bg-[#E5E1DC]"
          style={{ top: HALF }}
        />
        {lanes.impact.map((m) => tick(m, "impact"))}
        {lanes.event.map((m) => tick(m, "event"))}
      </div>
    </div>
  );
};

export default SessionTimelineStrip;
